// API 문서 패널 — 6+1개 엔드포인트 + 각 엔드포인트 인터랙티브 실행 + 웹훅 페이로드
import { useState } from 'react';
import { callEndpoint, type CallOptions, type CallResult } from '../lib/apiClient';
import { usePolicyStore } from '../lib/policyStore';
import { adminGet, adminPost, adminDelete } from '../lib/adminApi';

const API_KEY_STORAGE = 'fg_admin_api_key';

interface EndpointSpec {
  method: CallOptions['method'];
  path: string;
  desc: string;
  curlExample: string;
  responseExample: string;
  // 사용자 입력
  needsFile?: boolean;
  fields?: Array<{ name: string; label: string; default: string; type?: 'text' | 'textarea' }>;
  // 경로 파라미터 ('file_id', 'policy_id')
  pathParams?: string[];
}

const ENDPOINTS: EndpointSpec[] = [
  {
    method: 'POST',
    path: '/v1/scan',
    desc: '파일 검사 요청 (비동기)',
    curlExample: `curl -X POST https://fileguard.local/v1/scan \\
  -H "X-API-Key: fg_live_xxxxxxxx" \\
  -F "file=@suspicious.pdf" \\
  -F "policy_id=default" \\
  -F "callback_url=https://your-app.com/webhook/scan"`,
    responseExample: `{
  "file_id": "f_a3f8b2c1d9e4",
  "status": "pending",
  "queue_position": 3,
  "estimated_ms": 2400,
  "policy_applied": "default"
}`,
    needsFile: true,
    fields: [
      { name: 'policy_id', label: 'policy_id', default: 'default' },
      { name: 'callback_url', label: 'callback_url', default: 'https://your-app.com/webhook' },
    ],
  },
  {
    method: 'GET',
    path: '/v1/scan/:file_id',
    desc: '검사 결과 조회 (폴링)',
    curlExample: `curl https://fileguard.local/v1/scan/f_a3f8b2c1d9e4 \\
  -H "X-API-Key: fg_live_xxxxxxxx"`,
    responseExample: `{
  "file_id": "f_a3f8b2c1d9e4",
  "status": "safe",
  "engines": {
    "clamav": { "clean": true, "signatures": 0 },
    "yara": { "clean": true, "matched": [] }
  },
  "scan_time_ms": 1240
}`,
    pathParams: ['file_id'],
  },
  {
    method: 'GET',
    path: '/v1/quarantine',
    desc: '격리 파일 목록 조회',
    curlExample: `curl "https://fileguard.local/v1/quarantine?limit=20&offset=0" \\
  -H "X-API-Key: fg_live_xxxxxxxx"`,
    responseExample: `{
  "items": [
    {
      "file_id": "f_b2c1d9e4f5a3",
      "filename": "malware.zip",
      "threat": "Win.Trojan.Agent"
    }
  ],
  "total": 3
}`,
  },
  {
    method: 'POST',
    path: '/v1/quarantine/:file_id/action',
    desc: '격리 파일 처리 (삭제/재검사/허용)',
    curlExample: `curl -X POST https://fileguard.local/v1/quarantine/f_xxx/action \\
  -H "X-API-Key: fg_live_xxx" -H "Content-Type: application/json" \\
  -d '{"action":"delete","operator_id":"admin@company.com"}'`,
    responseExample: `{ "ok": true, "action": "delete" }`,
    pathParams: ['file_id'],
    fields: [
      {
        name: '__json__',
        label: 'JSON body',
        default: `{
  "action": "delete",
  "operator_id": "admin@company.com",
  "reason": "확인된 악성코드, 영구 삭제"
}`,
        type: 'textarea',
      },
    ],
  },
  {
    method: 'GET',
    path: '/v1/policies',
    desc: '정책 목록 조회',
    curlExample: `curl https://fileguard.local/v1/policies \\
  -H "X-API-Key: fg_live_xxxxxxxx"`,
    responseExample: `{ "items": [...] }`,
  },
  {
    method: 'PUT',
    path: '/v1/policies/:policy_id',
    desc: '정책 수정',
    curlExample: `curl -X PUT https://fileguard.local/v1/policies/default \\
  -H "X-API-Key: fg_live_xxx" -H "Content-Type: application/json" \\
  -d '{"maxSizeMB":1000}'`,
    responseExample: `{ "ok": true }`,
    pathParams: ['policy_id'],
    fields: [
      { name: '__json__', label: 'JSON body', default: `{ "maxSizeMB": 1000 }`, type: 'textarea' },
    ],
  },
  {
    method: 'GET',
    path: '/v1/health',
    desc: '서비스 헬스체크 (엔진/큐 상태)',
    curlExample: `curl https://fileguard.local/v1/health`,
    responseExample: `{
  "status": "healthy",
  "engines": { "clamav": "active", "yara": "active", "redis": "active" },
  "queue_size": 12,
  "uptime_seconds": 86400
}`,
  },
];

interface ApiClient { id: string; name: string; defaultPolicy: string | null; enabled: boolean; createdAt: string }
interface IssuedKey { clientId: string; apiKey: string; webhookSecret: string }

export function ApiPage() {
  const [apiKey, setApiKey] = useState<string>(() =>
    typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(API_KEY_STORAGE) ?? '' : '',
  );
  const settings = usePolicyStore((s) => s.settings);

  // API 클라이언트 관리
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newClientPolicy, setNewClientPolicy] = useState('default');
  const [issuedKey, setIssuedKey] = useState<IssuedKey | null>(null);
  const [creating, setCreating] = useState(false);

  function loadClients() {
    adminGet<{ items: ApiClient[] }>('/admin/clients')
      .then((d) => { setClients(d.items); setClientsLoaded(true); })
      .catch(() => setClientsLoaded(true));
  }

  function issueClient() {
    if (!newClientId || !newClientName) return;
    setCreating(true);
    adminPost<IssuedKey & { note: string }>('/admin/clients', { id: newClientId, name: newClientName, defaultPolicyId: newClientPolicy })
      .then((r) => {
        setIssuedKey(r);
        setNewClientId(''); setNewClientName('');
        loadClients();
      })
      .catch((e) => alert(String(e)))
      .finally(() => setCreating(false));
  }

  function disableClient(id: string) {
    adminDelete(`/admin/clients/${id}`).then(loadClients).catch(() => undefined);
  }

  function saveApiKey(v: string) {
    setApiKey(v);
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(API_KEY_STORAGE, v);
  }

  return (
    <>
      <div className="sect-head">
        <div className="sect-title">⟨⟩ API 문서 <em>REST API v1 · 인터랙티브 실행</em></div>
      </div>

      {/* API 클라이언트 발급 */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <div className="card-head-title">🏢 API 클라이언트 관리</div>
          <button className="btn btn-ghost btn-sm" onClick={loadClients}>{clientsLoaded ? '🔄 새로고침' : '목록 보기'}</button>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, marginBottom: 16, alignItems: 'end' }}>
            <div>
              <div className="form-label" style={{ marginBottom: 4 }}>클라이언트 ID</div>
              <input className="form-input mono" placeholder="my-service" value={newClientId} onChange={(e) => setNewClientId(e.target.value)} />
            </div>
            <div>
              <div className="form-label" style={{ marginBottom: 4 }}>이름</div>
              <input className="form-input" placeholder="내 서비스" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} />
            </div>
            <div>
              <div className="form-label" style={{ marginBottom: 4 }}>기본 정책</div>
              <input className="form-input mono" placeholder="default" value={newClientPolicy} onChange={(e) => setNewClientPolicy(e.target.value)} />
            </div>
            <button className="btn btn-pri btn-sm" onClick={issueClient} disabled={creating || !newClientId || !newClientName}>
              {creating ? '발급 중...' : '+ 발급'}
            </button>
          </div>

          {issuedKey && (
            <div style={{ background: 'rgba(0,217,163,.08)', border: '1px solid rgba(0,217,163,.25)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 600, marginBottom: 8 }}>⚠ API 키 발급 완료 — 지금 복사하세요. 다시 확인 불가능합니다.</div>
              <div className="form-label">API Key</div>
              <div className="text-mono" style={{ background: 'var(--bg2)', padding: '8px 12px', borderRadius: 6, fontSize: 13, wordBreak: 'break-all', marginBottom: 8, userSelect: 'all' }}>{issuedKey.apiKey}</div>
              <div className="form-label">Webhook Secret</div>
              <div className="text-mono" style={{ background: 'var(--bg2)', padding: '8px 12px', borderRadius: 6, fontSize: 13, wordBreak: 'break-all', userSelect: 'all' }}>{issuedKey.webhookSecret}</div>
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setIssuedKey(null)}>닫기</button>
            </div>
          )}

          {clientsLoaded && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text3)' }}>ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text3)' }}>이름</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text3)' }}>기본 정책</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--text3)' }}>상태</th>
                  <th style={{ padding: '6px 10px' }} />
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 && <tr><td colSpan={5} style={{ padding: 16, color: 'var(--text3)', textAlign: 'center' }}>클라이언트 없음</td></tr>}
                {clients.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px' }}><span className="text-mono">{c.id}</span></td>
                    <td style={{ padding: '8px 10px', color: 'var(--text2)' }}>{c.name}</td>
                    <td style={{ padding: '8px 10px' }}><span className="ext-chip">{c.defaultPolicy ?? '-'}</span></td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ color: c.enabled ? 'var(--accent)' : 'var(--text3)', fontSize: 12 }}>{c.enabled ? '활성' : '비활성'}</span>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {c.enabled && <button className="btn btn-danger btn-sm" onClick={() => disableClient(c.id)}>비활성화</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <div className="card-head-title">🔑 API 키 (이 세션에서 자동 적용)</div>
          <span className="text-mono text-muted">sessionStorage</span>
        </div>
        <div className="card-body">
          <div className="form-row" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="api-key">X-API-Key 헤더</label>
            <input
              id="api-key"
              className="form-input mono"
              placeholder="fg_live_xxxxxxxxxxxxxxxx"
              value={apiKey}
              onChange={(e) => saveApiKey(e.target.value)}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="text-mono text-muted" style={{ marginTop: 6 }}>
            ※ 모든 실행은 Vite proxy(<code>/v1</code> → <code>127.0.0.1:4400</code>)를 통해 백엔드로 전달됩니다. 백엔드가 실행 중이지 않으면 "연결 실패"가 표시됩니다.
          </div>
        </div>
      </div>

      {ENDPOINTS.map((e) => (
        <EndpointTryIt key={`${e.method} ${e.path}`} spec={e} apiKey={apiKey} />
      ))}

      <div className="card mt-20">
        <div className="card-head">
          <div className="card-head-title">🔐 Webhook 페이로드 형식</div>
          <span className="text-mono text-muted">현재 시크릿 적용</span>
        </div>
        <div className="code-block">
{`# 검사 완료 시 callback_url로 POST 발송
# Header: X-FileGuard-Signature: sha256=HMAC(${settings.webhookSecret.slice(0, 20)}..., body)

{
  "event": "scan.completed",
  "file_id": "f_a3f8b2c1d9e4",
  "status": "safe",
  "filename": "document.pdf",
  "size_bytes": 2459136,
  "sha256": "a3f8b2c1d9e4f5...",
  "engines": { "clamav": "clean", "yara": "clean" },
  "metadata": { "user_id": "u_123" },
  "scanned_at": "${new Date().toISOString()}"
}`}
        </div>
      </div>
    </>
  );
}

function methodCls(m: string): string {
  return m === 'POST' ? 'post' : m === 'GET' ? 'get' : m === 'PUT' ? 'put' : 'del';
}

function EndpointTryIt({ spec, apiKey }: { spec: EndpointSpec; apiKey: string }) {
  const [pathParams, setPathParams] = useState<Record<string, string>>(() => {
    const r: Record<string, string> = {};
    spec.pathParams?.forEach((p) => { r[p] = p === 'file_id' ? 'f_a3f8b2c1d9e4' : 'default'; });
    return r;
  });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const r: Record<string, string> = {};
    spec.fields?.forEach((f) => { r[f.name] = f.default; });
    return r;
  });
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CallResult | null>(null);
  const [running, setRunning] = useState(false);

  function resolvedPath(): string {
    let p = spec.path;
    if (spec.pathParams) {
      spec.pathParams.forEach((name) => { p = p.replace(`:${name}`, encodeURIComponent(pathParams[name] ?? '')); });
    }
    return p;
  }

  async function execute() {
    setRunning(true);
    setResult(null);
    const path = resolvedPath();
    const options: CallOptions = { method: spec.method, path, apiKey };

    if (spec.needsFile && file) {
      const form = new FormData();
      form.append('file', file);
      spec.fields?.forEach((f) => {
        if (f.name !== '__json__' && fieldValues[f.name] !== undefined) {
          form.append(f.name, fieldValues[f.name]!);
        }
      });
      options.formData = form;
    } else if (spec.fields?.some((f) => f.name === '__json__')) {
      try {
        options.jsonBody = JSON.parse(fieldValues['__json__'] ?? '{}');
      } catch {
        setResult({ ok: false, status: 0, body: '', error: 'JSON 파싱 실패 — body를 다시 확인하세요' });
        setRunning(false);
        return;
      }
    }

    const r = await callEndpoint(options);
    setResult(r);
    setRunning(false);
  }

  return (
    <div className="endpoint">
      <div className="endpoint-head">
        <span className={`method ${methodCls(spec.method)}`}>{spec.method}</span>
        <span className="endpoint-path">{spec.path}</span>
        <span className="endpoint-desc">{spec.desc}</span>
      </div>
      <div className="code-block">{spec.curlExample}</div>
      <div className="code-block" style={{ borderTop: '1px solid var(--border)', color: 'var(--text3)' }}>
        <span style={{ color: 'var(--text3)' }}># Example response</span>{'\n'}{spec.responseExample}
      </div>

      <div style={{ padding: 14, borderTop: '1px solid var(--border)', background: 'var(--bg2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span className="text-mono" style={{ color: 'var(--accent)', fontSize: 12 }}>▶ 실행</span>
          <span className="text-mono text-muted" style={{ fontSize: 12 }}>{spec.method} {resolvedPath()}</span>
        </div>

        {spec.pathParams?.map((name) => (
          <div className="form-row" key={name} style={{ marginBottom: 8 }}>
            <label className="form-label" htmlFor={`pp-${name}`}>경로 파라미터: <code>:{name}</code></label>
            <input
              id={`pp-${name}`}
              className="form-input mono"
              value={pathParams[name] ?? ''}
              onChange={(e) => setPathParams((p) => ({ ...p, [name]: e.target.value }))}
            />
          </div>
        ))}

        {spec.needsFile && (
          <div className="form-row" style={{ marginBottom: 8 }}>
            <label className="form-label" htmlFor={`file-${spec.path}`}>파일 (multipart)</label>
            <input
              id={`file-${spec.path}`}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="form-input"
            />
          </div>
        )}

        {spec.fields?.map((f) => (
          <div className="form-row" key={f.name} style={{ marginBottom: 8 }}>
            <label className="form-label" htmlFor={`f-${spec.path}-${f.name}`}>{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                id={`f-${spec.path}-${f.name}`}
                className="form-input mono"
                rows={4}
                value={fieldValues[f.name] ?? ''}
                onChange={(e) => setFieldValues((v) => ({ ...v, [f.name]: e.target.value }))}
                spellCheck={false}
              />
            ) : (
              <input
                id={`f-${spec.path}-${f.name}`}
                className="form-input mono"
                value={fieldValues[f.name] ?? ''}
                onChange={(e) => setFieldValues((v) => ({ ...v, [f.name]: e.target.value }))}
                spellCheck={false}
              />
            )}
          </div>
        ))}

        <button
          type="button"
          className="btn btn-pri btn-sm"
          onClick={execute}
          disabled={running}
          aria-label={`${spec.method} ${spec.path} 실행`}
        >
          {running ? '실행 중...' : '▶ 실행'}
        </button>

        {result && (
          <div
            style={{
              marginTop: 10,
              background: 'var(--bg)',
              border: `1px solid ${result.ok ? 'var(--accent)' : 'var(--red)'}`,
              borderRadius: 6,
              padding: 12,
            }}
            data-testid={`response-${spec.method}-${spec.path}`}
          >
            <div className="text-mono" style={{ fontSize: 12, color: result.ok ? 'var(--accent)' : 'var(--red)', marginBottom: 6 }}>
              {result.error
                ? `✗ ${result.error}`
                : `${result.ok ? '✓' : '✗'} HTTP ${result.status}`}
            </div>
            {result.body && (
              <pre className="text-mono" style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'pre-wrap', margin: 0 }}>
                {result.body}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
